import type { ComponentType } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { can } from '@seller/shared';
import { useSession } from './auth/useSession.js';
import { AppShell } from './components/AppShell.js';
import { UploadQueueProvider } from './imports/UploadQueue.js';
import { Alert, Spinner } from './components/ui.js';
import { LoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { InvitePage } from './pages/InvitePage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { TwoFactorPage } from './pages/TwoFactorPage.js';
import { ChangePasswordPage } from './pages/ChangePasswordPage.js';
import { AccountPage } from './pages/AccountPage.js';
import { HomePage } from './pages/HomePage.js';
import { ImportsPage } from './pages/ImportsPage.js';
import { BookingsPage } from './pages/BookingsPage.js';
import { SkusPage } from './pages/SkusPage.js';
import { SkuDetailPage } from './pages/SkuDetailPage.js';
import { FamilyDetailPage } from './pages/FamilyDetailPage.js';
import { ProductsPage } from './pages/ProductsPage.js';
import { ProductPage } from './pages/ProductPage.js';
import { TaxonomyReviewPage } from './pages/TaxonomyReviewPage.js';
import { RestockPage } from './pages/RestockPage.js';
import { PurchasePlanPage } from './pages/PurchasePlanPage.js';
import { DuplicatesPage } from './pages/DuplicatesPage.js';
import { KnownSellersPage } from './pages/KnownSellersPage.js';
import { ChartsPage } from './pages/ChartsPage.js';
import { AdminUsersPage } from './pages/AdminUsersPage.js';
import { EmailPage } from './pages/EmailPage.js';
import { LabelingPage } from './pages/LabelingPage.js';
import { LabelSettingsPage } from './pages/LabelSettingsPage.js';
import { LABELING_PATH, LABEL_SETTINGS_PATH } from './lib/nav.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

/**
 * Pages anyone can open, signed in or not, and even with the API down. They
 * are matched on the address before the session is consulted, so they never
 * turn into a sign-in prompt. The account-recovery pages live here: the
 * forgot-password form, and the invitation and reset links, whose token in the
 * address is the only credential they need.
 */
const PUBLIC_PAGES: Record<string, ComponentType> = {
  '/forgot': ForgotPasswordPage,
};

function publicPageFor(pathname: string): ComponentType | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  // The link pages carry a token segment, so they are matched by prefix.
  if (path.startsWith('/invite/')) return InvitePage;
  if (path.startsWith('/reset/')) return ResetPasswordPage;
  return PUBLIC_PAGES[path] ?? null;
}

/**
 * Routing is driven by session state, not by URL guards.
 *
 * The server decides what an account still has to do — enroll an authenticator,
 * enter a code, change a temporary password — and reports it on
 * `/api/auth/me`. Rendering straight from that means the client cannot
 * accidentally let someone past a step by navigating to a route directly,
 * because the whole authenticated tree simply is not mounted until the server
 * says the session is complete.
 */
export function App() {
  const session = useSession();

  const PublicPage = publicPageFor(window.location.pathname);
  if (PublicPage) return <PublicPage />;

  switch (session.status) {
    case 'loading':
      return (
        <div className="flex min-h-full items-center justify-center">
          <Spinner label="Loading" />
        </div>
      );

    case 'unreachable':
      return (
        <div className="mx-auto max-w-md px-6 py-16">
          <Alert>
            Cannot reach the API. If you are running locally, check that the server is up on port
            3010.
          </Alert>
        </div>
      );

    case 'signedOut':
      return <LoginPage />;

    case 'needsEnrollment':
      return <TwoFactorPage mode="enroll" />;

    case 'needsCode':
      return <TwoFactorPage mode="verify" />;

    case 'needsPasswordChange':
      return <ChangePasswordPage />;

    case 'signedIn':
      return (
        <BrowserRouter>
          <UploadQueueProvider>
            <AppShell user={session.user} unusedRecoveryCodes={session.unusedRecoveryCodes}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/imports" element={<ImportsPage />} />
                <Route path="/bookings" element={<BookingsPage />} />
                <Route path="/skus" element={<SkusPage />} />
                <Route path="/skus/:id" element={<SkuDetailPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/review" element={<TaxonomyReviewPage />} />
                {/* One product (a child ASIN), or an ASIN-less SKU as its own product. */}
                <Route path="/products/sku/:skuId" element={<ProductPage />} />
                <Route path="/products/:asin" element={<ProductPage />} />
                {/* Families is now the Products "Table" view; keep old links working. */}
                <Route path="/families" element={<Navigate to="/products?view=table" replace />} />
                <Route path="/families/detail" element={<FamilyDetailPage />} />
                <Route path="/restock" element={<RestockPage />} />
                <Route path="/purchase-plan" element={<PurchasePlanPage />} />
                <Route path="/duplicates" element={<DuplicatesPage />} />
                <Route path="/offers/sellers" element={<KnownSellersPage />} />
                <Route path="/charts" element={<ChartsPage />} />
                {/* FNSKU labels. A fixed path under /products that cannot be read
                  as an ASIN, so it never collides with a per-product page there. */}
                <Route path={LABELING_PATH} element={<LabelingPage />} />
                <Route path={LABEL_SETTINGS_PATH} element={<LabelSettingsPage />} />
                {/* Everyone has one, whatever their role: it is their own. */}
                <Route
                  path="/account"
                  element={
                    <AccountPage
                      user={session.user}
                      unusedRecoveryCodes={session.unusedRecoveryCodes}
                    />
                  }
                />
                {/* Rendered only for accounts that can manage users. The API
                  enforces the same rule, so this is navigation hygiene rather
                  than a security control. */}
                {can(session.user.role, 'users:manage') && (
                  <Route path="/users" element={<AdminUsersPage />} />
                )}
                {can(session.user.role, 'users:manage') && (
                  <Route path="/email" element={<EmailPage />} />
                )}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppShell>
          </UploadQueueProvider>
        </BrowserRouter>
      );
  }
}
